/**
 * Sense → Funnel, the screen itself. Slice 60.
 *
 * Split from `FunnelPage.tsx`, which fetches, so that `verify-sense-screens.tsx`
 * can render it: the page imports the API client, whose config reads
 * `import.meta.env` and evaluates only inside Vite — the split slices 13–17
 * made for the same reason.
 *
 * ## WHAT THE PAGE IS FOR
 *
 * One question — *of the people I can message, how many did my messages reach,
 * engage, and convert?* — per channel, over the last 30 days. Then the money:
 * what matched conversions were worth, and, beside it and never added to it,
 * what the workspace declared.
 *
 * ## EVERY STAGE IS DRAWN AGAINST CONTACTS
 *
 * Each bar, and the share printed beside it for anyone who cannot see the bar,
 * is a share of the channel's Contacts, not of the stage before. A funnel drawn
 * stage-on-stage implies each stage is a subset of the last, and Converted is
 * not a subset of Engaged — Wend-sent attribution asks whether a message came
 * first, not whether anyone opened it (`funnel-read.ts`). Contacts is everyone
 * reachable now; every other stage is the last 30 days, and the card says so,
 * because "1,287 contacts, 340 reached" otherwise reads as a quarter of the
 * list ever reached.
 *
 * ## "NOT MEASURED" IS A SENTENCE, NEVER A ZERO OR A BAR
 *
 * A stage the contract marks absent has no number to draw: `NoFigure` in the
 * figure slot, the reason beside it, and no bar — an empty bar reads as zero.
 * The absence carries its state: `not_set_up` (no conversion defined, tracking
 * off — the reader must act) is announced differently from `not_measured`
 * (Wend is waiting on reports, or does not read email replies).
 *
 * Setting up is asked for ONCE, linked: the Matched tile's absent line is the
 * link to Outcomes. Each Converted row says the same thing in a few words and
 * does not repeat the invitation (review, slice 60: four copies, one link).
 *
 * ## THE TIER TRAVELS WITH THE MONEY
 *
 * Every money figure carries its tier as a badge — Matched or Declared — the
 * concept's "each tagged with how much to trust it". The two totals sit in
 * separate tiles under their own names, so no reader can sum them by eye and
 * believe the sum. An Engaged figure that counts opens wears "Estimated":
 * some mail apps open everything, others block the image.
 *
 * ## THE BLUEPRINT'S PANELS AND METERS — SLICE 66
 *
 * The two money tiles are `MetricPanel`s: the kicker names the figure, the tier
 * badge sits in the panel's status slot, the count is the figure and the rupees
 * are its sub-line. The panel's figure is a union with no way to print a
 * number for an absent tile — its absent arm takes the `NoFigure` node, so the
 * "never a zero" rule above is the component's type, not this file's care.
 * Matched wears the brand wash because it is a measurement; Declared is plain,
 * because it is the workspace's own claim and should not out-rank it.
 *
 * The stage bars are the kit's `Meter` in the brand sweep rather than the
 * neutral `ProportionBar`: a share of Contacts is a magnitude, not a verdict,
 * and brand is the decoration colour that says so. They keep `data-role=
 * "funnel-bar"`, and are still drawn only for a measured stage. The counts are
 * sans and tabular; mono's dotted zero is the misreading a "0 converted" row
 * cannot afford.
 *
 * Not ported: the concept's drop-percentage funnel. Stages here are not
 * nested (see above), so a percentage lost between two stages would be
 * arithmetic over a subset relation that does not hold.
 */
import { Link } from 'react-router-dom';

import { Badge, Card, CardContent, CardHeader, CardTitle, Meter, MetricPanel, NoFigure, StateChip } from '@wend/ui';
import {
  FUNNEL_STAGE_LABEL,
  OUTCOME_TIER_LABEL,
  type Funnel,
  type FunnelAbsence,
  type FunnelChannel,
  type FunnelFigure,
} from '@wend/shared';

import { formatCount, formatRupees } from '../../lib/format';
import { MoreDetail } from '../templates/MoreDetail';

const CHANNEL_LABEL: Readonly<Record<FunnelChannel['channel'], string>> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
};

const PERCENT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });

/** "· 26.4%" of Contacts — the bar's meaning, in text. Nothing over an empty base. */
function share(people: number, contacts: number): string | null {
  return contacts > 0 ? PERCENT.format(people / contacts) : null;
}

export function FunnelContent({ funnel }: { funnel: Funnel }) {
  return (
    <>
      <section className="flex flex-col gap-3" aria-labelledby="funnel-money-title">
        <h2 id="funnel-money-title" className="text-[0.95rem] font-medium text-text">
          Conversions · last {funnel.windowDays} days
        </h2>
        <div className="grid gap-3 sm:grid-cols-2" data-role="funnel-money">
          <MoneyTile
            tier="matched"
            title="People who converted"
            figure={funnel.matched?.people ?? null}
            value={funnel.matched?.valueMinor ?? null}
            absence={funnel.convertedAbsence}
            caption={null}
            link={funnel.convertedAbsence?.state === 'not_set_up' || funnel.convertedAbsence?.state === 'not_measured'}
          />
          <MoneyTile
            tier="declared"
            title="Declared conversions"
            figure={funnel.declared?.quantity ?? null}
            value={funnel.declared?.valueMinor ?? null}
            absence={{ state: 'not_measured', reason: `Nothing declared for a period inside the last ${funnel.windowDays} days.` }}
            caption={`Periods wholly inside the last ${funnel.windowDays} days`}
            link={false}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="funnel-stages-title">
        <h2 id="funnel-stages-title" className="text-[0.95rem] font-medium text-text">
          Funnel · last {funnel.windowDays} days
        </h2>
        {funnel.channels.map((channel) => (
          <ChannelFunnel key={channel.channel} channel={channel} />
        ))}
      </section>

      <MoreDetail summary="How the funnel is counted">
        <p>
          Contacts is everyone you can message on the channel today; every other stage is the last{' '}
          {funnel.windowDays} days, as a share of Contacts.
        </p>
        <p>
          A matched conversion counts for a channel when Wend sent that person a message on it in the{' '}
          {funnel.attributionDays} days before the day they converted. That says the message came first, not
          that it caused the conversion — and it need not have been opened, so Converted can be larger than
          Engaged. Someone messaged on both channels counts on both. Declared conversions have no person, so
          no channel is credited with them.
        </p>
        <p>
          Engaged on email is an open, or a click on a link in it, with open or click tracking on. Wend counts a click
          when someone follows a link through Wend’s own address; links that differ per person, and the plain-text
          copy of the email, are not counted. Opens are approximate: some mail apps open every message, others block
          the image. On WhatsApp it is a read receipt, which people can turn off.
        </p>
        <p>
          Responded, on WhatsApp, is anyone who sent you a message within 7 days after one of yours. Someone with
          read receipts off can respond without counting as Engaged. Wend does not read replies to email.
        </p>
      </MoreDetail>
    </>
  );
}

function MoneyTile(props: {
  tier: 'matched' | 'declared';
  title: string;
  figure: number | null;
  value: number | null;
  absence: { state: FunnelAbsence; reason: string } | null;
  caption: string | null;
  link: boolean;
}) {
  const shown = props.figure !== null && props.value !== null;
  const absentState = props.absence?.state ?? 'not_measured';
  return (
    <MetricPanel
      data-tier={props.tier}
      variant={props.tier === 'matched' ? 'brand' : 'plain'}
      figureSize="lg"
      kicker={props.title}
      status={<Badge tone="neutral">{OUTCOME_TIER_LABEL[props.tier]}</Badge>}
      figure={
        shown && props.figure !== null
          ? { kind: 'measured', value: props.figure }
          : { kind: 'absent', node: <NoFigure state={absentState} /> }
      }
      sub={
        shown ? (
          <span className="flex flex-col gap-0.5">
            <span className="text-[0.95rem] font-medium tabular-nums text-text">worth {formatRupees(props.value ?? 0)}</span>
            {props.caption !== null && <span>{props.caption}</span>}
          </span>
        ) : props.link ? (
          <Link
            to="/sense/outcomes"
            className="text-[0.82rem] text-text underline underline-offset-4"
            data-role="funnel-setup-link"
          >
            {props.absence?.reason} Go to Outcomes
          </Link>
        ) : (
          props.absence?.reason
        )
      }
    />
  );
}

function ChannelFunnel({ channel }: { channel: FunnelChannel }) {
  const stages: { key: string; label: string; figure: FunnelFigure; extra?: string; estimated?: boolean }[] = [
    { key: 'reached', label: FUNNEL_STAGE_LABEL.reached, figure: channel.reached },
    {
      key: 'engaged',
      label: FUNNEL_STAGE_LABEL.engaged,
      figure: channel.engaged,
      estimated: channel.engagedBasis === 'opens' || channel.engagedBasis === 'opens_and_clicks',
    },
    { key: 'responded', label: FUNNEL_STAGE_LABEL.responded, figure: channel.responded },
    {
      key: 'converted',
      label: FUNNEL_STAGE_LABEL.converted,
      figure: channel.converted,
      ...(channel.convertedValueMinor === null ? {} : { extra: `worth ${formatRupees(channel.convertedValueMinor)}` }),
    },
  ];

  return (
    <Card data-role="funnel-channel" data-channel={channel.channel}>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">{CHANNEL_LABEL[channel.channel]}</CardTitle>
      </CardHeader>
      <CardContent className="p-[18px] pt-0">
        <dl className="flex flex-col gap-3">
          <div data-stage="contacts" className="grid grid-cols-[7rem_1fr] items-center gap-3 sm:grid-cols-[8rem_9rem_1fr]">
            <dt className="text-[0.88rem] text-text">{FUNNEL_STAGE_LABEL.contacts}</dt>
            <dd className="font-semibold tabular-nums text-text" data-role="funnel-contacts">
              {formatCount(channel.contacts)}
            </dd>
            <dd className="col-span-2 text-[0.8rem] text-muted sm:col-span-1">
              people with {channel.channel === 'email' ? 'an email address' : 'a phone number'}, today
            </dd>
          </div>
          {stages.map((stage) => (
            <div
              key={stage.key}
              data-stage={stage.key}
              data-measured={String(stage.figure.measured)}
              className="grid grid-cols-[7rem_1fr] items-center gap-3 sm:grid-cols-[8rem_9rem_1fr]"
            >
              <dt className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.88rem] text-text">
                {stage.label}
                {stage.key === 'converted' && stage.figure.measured && (
                  <Badge tone="neutral">{OUTCOME_TIER_LABEL.matched}</Badge>
                )}
              </dt>
              {stage.figure.measured ? (
                <>
                  <dd className="flex flex-wrap items-baseline gap-x-2 font-semibold tabular-nums text-text">
                    {formatCount(stage.figure.people)}
                    {share(stage.figure.people, channel.contacts) !== null && (
                      <span className="text-[0.74rem] font-normal text-muted">· {share(stage.figure.people, channel.contacts)}</span>
                    )}
                    {stage.estimated === true && <StateChip state="estimated" />}
                  </dd>
                  <dd className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                    <Meter
                      tone="brand"
                      size="md"
                      value={channel.contacts > 0 ? stage.figure.people / channel.contacts : 0}
                      data-role="funnel-bar"
                    />
                    {stage.extra !== undefined && (
                      <span className="text-[0.74rem] tabular-nums text-muted">{stage.extra}</span>
                    )}
                  </dd>
                </>
              ) : (
                <>
                  <dd>
                    <NoFigure state={stage.figure.state} />
                  </dd>
                  <dd className="col-span-2 text-[0.8rem] text-muted sm:col-span-1">{stage.figure.reason}</dd>
                </>
              )}
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
